import {
  FormControl,
  Grid,
  Input,
  InputLabel,
  InputAdornment,
  Typography,
  Select,
  Button,
  MenuItem,
} from "@material-ui/core";
import React, { useContext, useState, useEffect } from "react";

import Papa from "papaparse";
import UploadIcon from "@material-ui/icons/Publish";
import { useTranslation } from "react-i18next";
import { validateEmail } from "../../services/misc.service";
import { DeploymentContext, FleetContext } from "../../context";
import { SectionGridContainer, SectionGridLayout } from "../../components/Layout";
import { FilledTextField } from "../../components/FormElements";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { ConfirmationModal } from "../../components/Modals";

const DEFAULT_CUSTOMER = "Link";

const RegisterVehicles = () => {
  const { registerFromCsv, registerApiResponse } = useContext(DeploymentContext);
  const { fleets, fetchFleets } = useContext(FleetContext);
  const { t } = useTranslation();
  const [emailDirty, setEmailDirty] = useState(false);
  const [email, setEmail] = useState("");
  const [fleetName, setFleetName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingFleets, setLoadingFleets] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [vehicleFINs, setVehicleFINs] = useState([]);
  const cluster = window.REACT_APP_CLUSTER_NAME;

  // Fetch fleets once on page load if they are not there
  useEffect(() => {
    if (!fleets || !fleets.size) {
      setLoadingFleets(true);
      fetchFleets();
    }
  }, []);

  // Select first fleet in dropdown
  useEffect(() => {
    if (fleets && fleets.size) {
      setFleetName(Array.from(fleets.values())[0].name);
    }
    setLoadingFleets(false);
  }, [fleets]);

  const openModal = () => {
    Papa.parse(selectedFile, {
      header: false,
      async complete(results) {
        const { data } = results;
        const FINs = [];
        data.forEach((row) => {
          // NOTE: requires user to upload files with no headers
          const [fin] = row;
          if (fin) FINs.push(fin);
        });
        setVehicleFINs(FINs);
      },
    });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const submit = async () => {
    setLoadingSubmit(true);
    handleModalClose();
    try {
      if (!validateEmail(email)) throw new Error("Invalid Email");
      await registerFromCsv(vehicleFINs, DEFAULT_CUSTOMER, fleetName, [email], cluster); // provide emails as an array
      setLoadingSubmit(false);
    } catch (e) {
      console.error(e);
      setLoadingSubmit(false);
    }
  };

  if (loadingSubmit || loadingFleets) {
    return <LoadingOverlay />;
  }
  return (
    <SectionGridLayout>
      <SectionGridContainer spaceContents>
        <Grid item xs={12}>
          <Typography variant="h5">{t("RegisterVehicles.Title")}</Typography>
          <a
            className="spBlue"
            href="https://superpedestrian.atlassian.net/wiki/spaces/CS/pages/1522630939/Register+Vehicles+-+Instructions+for+Use"
          >
            Instructions For Use
          </a>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <InputLabel>FIN csv file</InputLabel>
            <Input
              type="file"
              id="uploadCSV"
              accept="text/csv"
              onChange={(event) => setSelectedFile(event.target.files[0])}
              startAdornment={
                <InputAdornment position="start">
                  <UploadIcon data-testid="upload-icon" />
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <InputLabel>Destination Fleet</InputLabel>
            <Select
              label="Fleet"
              value={fleetName}
              onChange={(event) => setFleetName(event.target.value)}
            >
              {fleets &&
                Array.from(fleets.entries()).map(([fleetId, fleet]) => (
                  <MenuItem key={fleetId} value={fleet.name}>
                    {fleet.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <FilledTextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              label={t("RegistrationViews.RecipientEmail")}
              error={emailDirty && !validateEmail(email)}
              onBlur={() => setEmailDirty(true)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <Button disabled={!selectedFile || !fleetName || !email} onClick={() => openModal()}>
              Submit
            </Button>
          </FormControl>
        </Grid>
        <ConfirmationModal
          title={t("Fleet.Deployment.ConfirmRegistrationTitle")}
          content={t("Fleet.Deployment.RegistrationWarning", {
            totalFINs: vehicleFINs.length,
            fleetName,
          })}
          submit={submit}
          modalOpen={modalOpen}
          handleModalClose={handleModalClose}
        />
        <Grid item xs={12}>
          {!!registerApiResponse && (
            <div>
              Vehicle Api Responses<br></br>
              <pre>{JSON.stringify(registerApiResponse, undefined, 4)}</pre>
            </div>
          )}
        </Grid>
      </SectionGridContainer>
    </SectionGridLayout>
  );
};

export default RegisterVehicles;
